import { useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, UploadCloud } from 'lucide-react';
import { labApi } from '@/lib/api';
import { Select } from '@/components/ui/Select';
import { LabConfiguratorShell } from './LabConfiguratorShell';
import { GenerationPanel } from './GenerationPanel';

interface ImportProposal {
  id: string;
  fieldName: string;
  fieldType: 'user_input' | 'field' | 'choice';
  title: string;
  value: string;
  sourceExcerpt: string;
  confidence: number;
  required: boolean;
  options: Array<{ value: string; label: string }>;
}

interface LabContentImportModalProps {
  productId: string;
  pipeline: 'ADVERTORIAL' | 'LISTICALE' | 'QUIZ' | 'LANDING';
  initialInputs?: Record<string, string>;
  onScratch: () => void;
  onClose: () => void;
  onComplete: () => void;
}

type Stage = 'source' | 'analyzing' | 'review' | 'applying' | 'generating';

function sourcePreview(value: string, maxChars = 700): string {
  return value.length > maxChars ? `${value.slice(0, maxChars).trimEnd()}…` : value;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { data?: { error?: string } } }).response?.data?.error || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

export function LabContentImportModal({ productId, pipeline, initialInputs, onScratch, onClose, onComplete }: LabContentImportModalProps) {
  const [mode, setMode] = useState<'scratch' | 'file'>('scratch');
  const [stage, setStage] = useState<Stage>('source');
  const [file, setFile] = useState<File | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [contentId, setContentId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [proposals, setProposals] = useState<ImportProposal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const missing = useMemo(() => proposals.filter(item => item.required && !item.value.trim()), [proposals]);
  const recognizedCount = useMemo(
    () => proposals.filter(proposal => proposal.sourceExcerpt.trim()).length,
    [proposals],
  );

  useEffect(() => {
    if (!importId || stage !== 'analyzing') return;
    const timer = window.setInterval(async () => {
      try {
        const response = await labApi.getPipelineImport(importId);
        const data = response.data.data;
        setProgress(data.progress || 0);
        if (data.status === 'READY') {
          setProposals((data.proposals || []).map((proposal: ImportProposal) => ({
            ...proposal,
            value: initialInputs?.[proposal.fieldName] ?? proposal.value,
          })));
          setStage('review');
        } else if (data.status === 'FAILED' || data.status === 'STALE') {
          setError(data.error || 'Analiza dokumentu nie powiodła się.');
          setStage('source');
        }
      } catch (pollError) {
        setError(errorMessage(pollError, 'Nie udało się odczytać postępu analizy.'));
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [importId, stage]);

  const acceptFile = (next?: File | null) => {
    if (!next) return;
    const extension = next.name.toLowerCase().split('.').pop();
    if (!['txt', 'md'].includes(extension || '') || next.size < 1 || next.size > 2 * 1024 * 1024) {
      setFile(null);
      setError('Wybierz niepusty plik TXT lub MD o rozmiarze do 2 MiB.');
      return;
    }
    setFile(next);
    setError(null);
  };

  const handleNext = async () => {
    if (mode === 'scratch') {
      onScratch();
      return;
    }
    if (!file) {
      setError('Dodaj jeden plik TXT lub MD.');
      return;
    }
    try {
      setStage('analyzing');
      setProgress(1);
      const response = await labApi.startPipelineImport(productId, pipeline, file);
      setImportId(response.data.data.id);
    } catch (startError) {
      setStage('source');
      setError(errorMessage(startError, 'Nie udało się rozpocząć importu.'));
    }
  };

  const handleApply = async () => {
    if (!importId || missing.length > 0) return;
    try {
      setStage('applying');
      const response = await labApi.applyPipelineImport(importId, proposals.map(item => ({ id: item.id, value: item.value })));
      setContentId(response.data.data.targetId);
      setStage('generating');
    } catch (applyError) {
      const message = errorMessage(applyError, 'Nie udało się zatwierdzić importu.');
      const stale = applyError && typeof applyError === 'object' && 'response' in applyError
        && (applyError as { response?: { status?: number } }).response?.status === 409
        && message.includes('Konfiguracja pipeline');
      setStage(stale ? 'source' : 'review');
      if (stale) setImportId(null);
      setError(message);
    }
  };

  if (stage === 'generating' && contentId) {
    return (
      <GenerationPanel
        productId={productId}
        mode={pipeline}
        contentId={contentId}
        kbName=""
        isFirstGeneration={false}
        onBack={onClose}
        onComplete={onComplete}
        onError={message => { setError(message); setStage('source'); }}
      />
    );
  }

  if (stage === 'analyzing' || stage === 'applying') {
    return (
      <LabConfiguratorShell
        title={stage === 'analyzing' ? 'Analizuję stronę' : 'Zapisuję checkpointy'}
        description="Dalsze generowanie obejmie wyłącznie brakujące elementy."
        icon={<Loader2 className="h-6 w-6 animate-spin" />}
        onClose={onClose}
        closeDisabled
      >
        <div className="py-8">
          <div className="mb-2 flex justify-between text-sm text-text-secondary"><span>Postęp</span><span>{stage === 'analyzing' ? progress : 100}%</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-hover"><div className="h-full rounded-full bg-accent-primary" style={{ width: `${stage === 'analyzing' ? progress : 100}%` }} /></div>
        </div>
      </LabConfiguratorShell>
    );
  }

  if (stage === 'review') {
    return (
      <LabConfiguratorShell
        title="Sprawdź import strony"
        description="Rozpoznane fragmenty są edytowalne. Puste sekcje wygeneruje istniejący pipeline."
        icon={<FileText className="h-6 w-6" />}
        onClose={onClose}
        size="wide"
        footer={(
          <div className="flex items-center justify-between gap-4">
            <button type="button" onClick={handleApply} disabled={missing.length > 0} className="btn-primary disabled:opacity-50">Zatwierdź i generuj braki</button>
            <button type="button" onClick={onClose} className="btn-secondary">Anuluj</button>
          </div>
        )}
      >
        {error && <p className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}
        {missing.length > 0 && <p className="rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning">Uzupełnij: {missing.map(item => item.title).join(', ')}.</p>}
        <div className={`rounded-lg border p-3 text-sm ${recognizedCount > 0 ? 'border-accent-primary/25 bg-accent-primary/10 text-text-secondary' : 'border-danger/25 bg-danger/10 text-danger'}`}>
          Rozpoznano dane dla <strong>{recognizedCount} z {proposals.length}</strong> pól pipeline
          {proposals.length > 0 ? ` (${Math.round((recognizedCount / proposals.length) * 100)}%)` : ''}.
          {recognizedCount > 0
            ? ' Puste sekcje zostaną przygotowane przez standardowy pipeline po zatwierdzeniu.'
            : ' Dokument nie został poprawnie dopasowany. Nie zatwierdzaj importu i spróbuj ponownie.'}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {proposals.map(proposal => (
            <section key={proposal.id} className="rounded-xl border border-border bg-surface-hover/30 p-4">
              <div className="mb-3 flex justify-between gap-3"><h3 className="text-sm font-semibold text-text-primary">{proposal.title}</h3><span className="text-xs text-text-muted">{Math.round(proposal.confidence * 100)}%</span></div>
              {proposal.options.length > 0 ? (
                <Select value={proposal.value} onChange={event => setProposals(current => current.map(item => item.id === proposal.id ? { ...item, value: event.target.value } : item))}>
                  <option value="">Wybierz wartość</option>
                  {proposal.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Select>
              ) : (
                <textarea value={proposal.value} onChange={event => setProposals(current => current.map(item => item.id === proposal.id ? { ...item, value: event.target.value } : item))} rows={5} className="w-full resize-y rounded-lg border border-border bg-surface-raised p-3 text-sm text-text-primary focus:border-accent-primary focus:outline-none" />
              )}
              {proposal.sourceExcerpt && <blockquote className="mt-3 whitespace-pre-wrap break-words border-l-2 border-accent-primary/40 pl-3 text-xs text-text-muted">Źródło: {sourcePreview(proposal.sourceExcerpt)}</blockquote>}
            </section>
          ))}
        </div>
      </LabConfiguratorShell>
    );
  }

  return (
    <LabConfiguratorShell
      title="Nowy wariant strony"
      description="Wybierz generowanie od zera albo import istniejącej treści TXT/MD."
      icon={<FileText className="h-6 w-6" />}
      onClose={onClose}
      footer={<div className="flex items-center justify-between gap-4"><button type="button" onClick={handleNext} className="btn-primary">Dalej</button><button type="button" onClick={onClose} className="btn-secondary">Anuluj</button></div>}
    >
      {error && <p className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        <button type="button" onClick={() => setMode('scratch')} className={`rounded-xl border p-4 text-left ${mode === 'scratch' ? 'border-accent-primary bg-accent-primary/10' : 'border-border'}`}><span className="block text-sm font-semibold text-text-primary">Generuj od zera</span><span className="mt-1 block text-xs text-text-muted">Użyj standardowego konfiguratora pipeline’u.</span></button>
        <button type="button" onClick={() => setMode('file')} className={`rounded-xl border p-4 text-left ${mode === 'file' ? 'border-accent-primary bg-accent-primary/10' : 'border-border'}`}><span className="block text-sm font-semibold text-text-primary">Importuj TXT/MD</span><span className="mt-1 block text-xs text-text-muted">Zachowaj istniejące fragmenty jako checkpointy.</span></button>
      </div>
      {mode === 'file' && (
        <label onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); acceptFile(event.dataTransfer.files[0]); }} className="flex cursor-pointer flex-col items-center rounded-xl border border-dashed border-accent-primary/40 bg-accent-primary/5 p-8 text-center">
          <UploadCloud className="mb-3 h-8 w-8 text-accent-primary" /><span className="text-sm font-medium text-text-primary">{file?.name || 'Upuść plik lub kliknij'}</span><span className="mt-1 text-xs text-text-muted">TXT lub MD, do 2 MiB</span>
          <input type="file" accept=".txt,.md,text/plain,text/markdown" className="sr-only" onChange={event => acceptFile(event.target.files?.[0])} />
        </label>
      )}
    </LabConfiguratorShell>
  );
}
